import { useEffect, useMemo, useState } from "react";
import Icon from "@/components/Icon";
import Button from "@/components/Button";
import InstructionPanel from "@/components/InstructionPanel";
import { colorVar } from "@/components/CourseContextBar";
import { listAllCourses } from "@/api/course";
import { listExercises, getExercise, copyExerciseToMine } from "@/api/exercises";
import type { LibraryCourse, ExerciseSummary, ExerciseDetail } from "@/types";

interface Props {
  /** Cours actif (destination des copies). */
  currentCourseId: number;
  onClose: () => void;
  /** Appelé après une copie réussie (rafraîchit la sidebar du cours courant). */
  onCopied: () => void;
}

/** Bibliothèque inter-profs : explorer le contenu publié des autres cours (regroupés
 *  par enseignant), prévisualiser un exercice, et le copier dans le cours actif —
 *  plusieurs fois, sans fermer le panneau. */
export default function ExplorerModal({ currentCourseId, onClose, onCopied }: Props) {
  const [courses, setCourses] = useState<LibraryCourse[]>([]);
  const [sel, setSel] = useState<LibraryCourse | null>(null);
  const [openTeachers, setOpenTeachers] = useState<Set<string>>(new Set());
  const [exercises, setExercises] = useState<ExerciseSummary[] | null>(null);
  const [preview, setPreview] = useState<ExerciseDetail | null>(null);
  const [copying, setCopying] = useState<number | null>(null);
  const [justCopied, setJustCopied] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { listAllCourses().then(setCourses).catch(() => {}); }, []);

  // Regroupe par enseignant ; mes cours d'abord, puis ordre alphabétique.
  const groups = useMemo(() => {
    const m = new Map<string, LibraryCourse[]>();
    for (const c of courses) {
      const key = c.teacher ?? "—";
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(c);
    }
    return [...m.entries()].sort(([ta, ca], [tb, cb]) => {
      const ma = ca.some((c) => c.mine) ? 0 : 1;
      const mb = cb.some((c) => c.mine) ? 0 : 1;
      return ma - mb || ta.localeCompare(tb);
    });
  }, [courses]);

  // Ouvre par défaut le groupe « moi » (sinon le premier).
  useEffect(() => {
    if (groups.length && openTeachers.size === 0) {
      setOpenTeachers(new Set([groups[0][0]]));
    }
  }, [groups]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sel) { setExercises(null); return; }
    setExercises(null);
    listExercises({ course_id: sel.id }).then(setExercises).catch(() => setExercises([]));
  }, [sel]);

  const toggleTeacher = (t: string) =>
    setOpenTeachers((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });

  const copy = async (ex: ExerciseSummary) => {
    setCopying(ex.id); setError(null);
    try {
      await copyExerciseToMine(ex.id, currentCourseId);
      onCopied();  // rafraîchit la sidebar ; le panneau reste ouvert
      setJustCopied((prev) => new Set(prev).add(ex.id));
      setTimeout(() => setJustCopied((prev) => {
        const n = new Set(prev); n.delete(ex.id); return n;
      }), 1600);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Échec de la copie.");
    } finally {
      setCopying(null);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Bibliothèque">
        <div style={styles.head}>
          <h2 style={styles.title}>Explorer la bibliothèque</h2>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Fermer"><Icon name="clear" size={18} /></button>
        </div>
        {error && <div style={styles.error}><Icon name="alert" size={15} style={{ color: "var(--red)", flexShrink: 0 }} /> {error}</div>}

        <div style={styles.body}>
          {/* Colonne enseignants → cours */}
          <div style={styles.courseCol}>
            <span style={styles.colLabel}>Enseignants</span>
            {groups.map(([teacher, list]) => {
              const open = openTeachers.has(teacher);
              return (
                <div key={teacher}>
                  <button style={styles.teacherHead} onClick={() => toggleTeacher(teacher)} aria-expanded={open}>
                    <Icon name={open ? "expanded" : "collapsed"} size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
                    <span style={styles.teacherName}>{teacher}</span>
                    {list.some((c) => c.mine) && <span style={styles.mineTag}>moi</span>}
                    <span style={styles.teacherCount}>{list.length}</span>
                  </button>
                  {open && list.map((c) => (
                    <button key={c.id} style={{ ...styles.courseItem, ...(sel?.id === c.id ? styles.courseItemActive : {}) }}
                            onClick={() => setSel(c)}>
                      <span style={{ ...styles.dot, background: colorVar(c.color) }} />
                      <span style={styles.courseItemName}>{c.title}</span>
                    </button>
                  ))}
                </div>
              );
            })}
            {courses.length === 0 && <span style={styles.empty}>Aucun cours.</span>}
          </div>

          {/* Colonne exercices */}
          <div style={styles.exCol}>
            {!sel ? (
              <p style={styles.placeholder}>Choisis un cours pour voir ses séries publiées.</p>
            ) : exercises === null ? (
              <p style={styles.placeholder}>Chargement…</p>
            ) : exercises.length === 0 ? (
              <p style={styles.placeholder}>Aucune série publiée dans « {sel.title} ».</p>
            ) : (
              exercises.map((ex) => {
                const done = justCopied.has(ex.id);
                return (
                  <div key={ex.id} style={styles.exCard}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={styles.exTitle}>{ex.title}</h3>
                      <p style={styles.exMeta}>
                        {ex.degrees.map((d) => d.label).join(", ") || "—"} · {ex.step_count} exercice{ex.step_count > 1 ? "s" : ""}
                      </p>
                    </div>
                    <button style={styles.previewBtn} onClick={() => getExercise(ex.id).then(setPreview)} type="button" title="Aperçu">
                      <Icon name="search" size={14} />
                    </button>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={copying === ex.id}
                      style={done ? { background: "var(--green)" } : undefined}
                      onClick={() => copy(ex)}
                    >
                      {copying === ex.id
                        ? <span className="spin" />
                        : done
                          ? <><Icon name="done" size={13} /> Copié</>
                          : <><Icon name="duplicate" size={13} /> Copier ici</>}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Volet d'aperçu */}
        {preview && (
          <div style={styles.drawer} onClick={() => setPreview(null)}>
            <div style={styles.drawerInner} onClick={(e) => e.stopPropagation()}>
              <div style={styles.drawerHead}>
                <span style={styles.drawerTitle}>{preview.title}</span>
                <button style={styles.closeBtn} onClick={() => setPreview(null)} aria-label="Fermer l'aperçu"><Icon name="clear" size={16} /></button>
              </div>
              <div style={styles.drawerBody}>
                {preview.steps.map((s, i) => (
                  <div key={s.id} style={styles.previewStep}>
                    <div style={styles.previewStepTitle}>Exercice {i + 1} — {s.title || "Sans titre"}</div>
                    <InstructionPanel step={s} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  panel: { position: "relative", background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-1)", width: "min(900px, 100%)", height: "min(640px, 90vh)", display: "flex", flexDirection: "column", overflow: "hidden" },
  head: { display: "flex", alignItems: "center", padding: "16px 18px", borderBottom: "1px solid var(--border-soft)" },
  title: { margin: 0, fontSize: 17, fontWeight: 600, color: "var(--text)", flex: 1 },
  closeBtn: { background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4, display: "inline-flex" },
  error: { display: "flex", alignItems: "center", gap: 8, margin: "12px 18px 0", padding: "8px 12px", background: "var(--err-box-bg)", color: "var(--red)", borderRadius: "var(--radius)", fontSize: 13 },
  body: { flex: 1, display: "flex", minHeight: 0 },
  courseCol: { width: 260, flexShrink: 0, borderRight: "1px solid var(--border-soft)", overflowY: "auto", padding: 10, display: "flex", flexDirection: "column", gap: 2 },
  colLabel: { fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)", padding: "2px 6px 6px" },
  teacherHead: { display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", background: "transparent", border: "none", borderRadius: "var(--radius-sm)", color: "var(--text)", padding: "7px 8px", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  teacherName: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  teacherCount: { fontSize: 11, color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" },
  mineTag: { fontSize: 10, color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 999, padding: "1px 6px" },
  courseItem: { display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: "transparent", border: "none", borderRadius: "var(--radius-sm)", color: "var(--text)", padding: "6px 10px 6px 22px", cursor: "pointer", fontSize: 13 },
  courseItemActive: { background: "var(--bg-base)" },
  courseItemName: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  dot: { width: 11, height: 11, borderRadius: "50%", flexShrink: 0 },
  exCol: { flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 },
  placeholder: { color: "var(--text-faint)", fontSize: 13, fontStyle: "italic" },
  exCard: { display: "flex", alignItems: "center", gap: 10, background: "var(--bg-base)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius)", padding: "10px 14px" },
  exTitle: { margin: 0, fontSize: 14, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  exMeta: { margin: "2px 0 0", fontSize: 12, color: "var(--text-muted)" },
  previewBtn: { background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text-muted)", cursor: "pointer", padding: "5px 8px", display: "inline-flex", flexShrink: 0 },
  drawer: { position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", justifyContent: "flex-end" },
  drawerInner: { width: "min(460px, 90%)", height: "100%", background: "var(--bg-panel)", borderLeft: "1px solid var(--border)", display: "flex", flexDirection: "column" },
  drawerHead: { display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: "1px solid var(--border-soft)" },
  drawerTitle: { flex: 1, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  drawerBody: { flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 16 },
  previewStep: { display: "flex", flexDirection: "column", gap: 6 },
  previewStepTitle: { fontSize: 12, fontWeight: 700, color: "var(--blue)", textTransform: "uppercase", letterSpacing: 0.4 },
  empty: { color: "var(--text-faint)", fontSize: 13, fontStyle: "italic", padding: 6 },
};
